import { z } from 'zod';

/**
 * Extras schema of an environment (BB-58, CARD-004): which key-value extras a channel's messages carry, with a type,
 * allowed values, whether they are required (optionally only when another extra has a value) and a description.
 * The composer builds its form from it; saving content checks the values. Messages still reach the SDKs as raw strings
 * (`extras`) plus the typed copy (`typedExtras`).
 */
export const EXTRAS_CHANNELS = ['content_card', 'push', 'in_app', 'banner'] as const;
export type ExtrasChannel = (typeof EXTRAS_CHANNELS)[number];
export const EXTRA_TYPES = ['string', 'enum', 'enum_list', 'number', 'boolean', 'url', 'deeplink', 'json'] as const;
export type ExtraType = (typeof EXTRA_TYPES)[number];

export const ExtraKey = z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,99}$/, 'lowercase letters, digits and _ . -, up to 100 chars');
const EnumValue = z.string().trim().min(1).max(100).regex(/^[^,]+$/, 'no commas (lists are comma-separated)');

/** Only when another extra has one of these values (e.g. `bonus-type` when `banner-type` is `bonus`). */
export const ExtraCondition = z.strictObject({ key: ExtraKey, in: z.array(z.string().min(1).max(100)).min(1).max(50) });
export type ExtraCondition = z.infer<typeof ExtraCondition>;

const definitionFields = {
  type: z.enum(EXTRA_TYPES),
  enumValues: z.array(EnumValue).max(100).default([]),
  required: z.boolean().default(false),
  description: z.string().trim().max(500).nullable().default(null),
  deprecated: z.boolean().default(false),
  appliesWhen: ExtraCondition.nullable().default(null),
};

const enumRules = (d: { type?: ExtraType; enumValues?: string[] }, ctx: z.RefinementCtx) => {
  const isEnum = d.type === 'enum' || d.type === 'enum_list';
  if (d.type && isEnum && (d.enumValues?.length ?? 0) === 0) ctx.addIssue({ code: 'custom', path: ['enumValues'], message: `${d.type} needs at least one value` });
  if (d.type && !isEnum && (d.enumValues?.length ?? 0) > 0) ctx.addIssue({ code: 'custom', path: ['enumValues'], message: `only enum and enum_list have values` });
  if (d.enumValues && new Set(d.enumValues).size !== d.enumValues.length) ctx.addIssue({ code: 'custom', path: ['enumValues'], message: 'values repeat' });
};

export const CreateExtraBody = z.strictObject({ channel: z.enum(EXTRAS_CHANNELS), key: ExtraKey, ...definitionFields }).superRefine(enumRules);
export type CreateExtraBody = z.infer<typeof CreateExtraBody>;

export const UpdateExtraBody = z
  .strictObject({
    type: definitionFields.type.optional(),
    enumValues: z.array(EnumValue).max(100).optional(),
    required: z.boolean().optional(),
    description: z.string().trim().max(500).nullable().optional(),
    deprecated: z.boolean().optional(),
    appliesWhen: ExtraCondition.nullable().optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), 'nothing to change');
export type UpdateExtraBody = z.infer<typeof UpdateExtraBody>;

export const ExtrasSchemaQuery = z.object({ channel: z.enum(EXTRAS_CHANNELS).default('content_card') });

export const SetExtrasStrictBody = z.strictObject({ channel: z.enum(EXTRAS_CHANNELS), strict: z.boolean() });
export type SetExtrasStrictBody = z.infer<typeof SetExtrasStrictBody>;

export const ValidateExtrasBody = z.strictObject({ channel: z.enum(EXTRAS_CHANNELS).default('content_card'), extras: z.record(z.string().min(1).max(100), z.string().max(1000)) });
export type ValidateExtrasBody = z.infer<typeof ValidateExtrasBody>;

export interface ExtraDefinitionView {
  id: string;
  channel: ExtrasChannel;
  key: string;
  type: ExtraType;
  enumValues: string[];
  required: boolean;
  description: string | null;
  deprecated: boolean;
  appliesWhen: ExtraCondition | null;
  updatedAt: string;
}

export interface ExtrasSchemaView {
  channel: ExtrasChannel;
  /** Unknown keys are errors (otherwise warnings); `environment.settings.extrasStrict[channel]`. */
  strict: boolean;
  items: ExtraDefinitionView[];
}

export interface ExtraIssue {
  path: string;
  code: 'unknown_extra' | 'deprecated_extra' | 'invalid_type' | 'not_allowed_value' | 'required_extra' | 'not_applicable_extra';
  message: string;
}

export interface ExtrasCheck {
  ok: boolean;
  errors: ExtraIssue[];
  warnings: ExtraIssue[];
  /** Values typed by the schema (keys with Liquid or unknown keys stay out). */
  typed: Record<string, string | number | boolean | string[]>;
}
